import { QuestionCriteria, QuizAnswerMap, QuizState } from '@/lib/apis/types'
import type { QuizScreen } from './quiz-screen'
import { atLeastOneIsSelected, isTrue } from '@/features/quiz/utils'

export type ScreenWithState = {
  screen: QuizScreen
  disabled: boolean
}

// One idea is to have a list of lists/groups as the quiz flow.
// Within a group, for most of the time - it will have a root quiz which can
// switch on/off the other questions within the group.
// This makes it fast to switch questions on/off as we only need to traverse
// within a subgroup, and those after the most recently interacted question.
// This does not alleviate the computation requirement when
// calculating current question using the offset value in the beginning. After
// initialization, we can hold onto the current question's actual offset in the pointer
// given back to the caller. So after every next, and prev, we can use this
// pointer and start traversing front or back. And it's easier that way.
//
//
// ScreenComposer is an abstract of that idea, and each treatment quiz will inherit
// from this class as every quiz will have their own computation functions.
// Note that the dynamic quiz only holds the question IDs, and it is up to other
// classes to render the screen based on the ID returned from this class.
export default abstract class QuizScreenGraphOrganizer {
  initialized: boolean
  groups: Array<Array<ScreenWithState>>
  criteriaMap: CriteriaFnMap

  protected constructor(screenGroups: Array<Array<QuizScreen>>) {
    this.initialized = false
    this.groups = screenGroups.map((group) =>
      group.map((screen) => ({
        screen,
        disabled: true,
      })),
    )

    // Initialise criteria map
    this.criteriaMap = {}
    this.initializeCriteriaFnMap(screenGroups)
  }

  // Initialize the quiz based on the state returned from the server. Here we
  // do the initial heavy lifting of calculating which question the user is on.
  // I thought about doing this in the constructor, but we still need to return
  // the pointer to the question the user is on after initialization.
  //
  // In some cases, we might need to initialize twice. Like when the user has
  // already done some parts of the quiz, without logging in,
  // and during the log-in step, they logged in and accepted to
  // continue previous quiz.
  // And depending on the user's login state, we might have to switch a few
  // things in the quiz.
  abstract initialize(loggedIn: boolean, state?: QuizState): PointerWithOffset

  // Calculate which quiz should be shown depending on
  // 1) The current question user is on
  // 2) The answers user have selected so far
  // 3) Whether the user has interacted with the current question
  abstract next(
    current: PointerWithOffset,
    answers: QuizAnswerMap,
    interacted: EnrichedData<boolean>,
  ): PointerWithOffset

  // Going to the previous question after updating the further screens does not
  // require updating the screen state. We can treat this as an invariant.
  abstract prev(current: PointerWithOffset): PointerWithOffset

  // Refresh all the questions from start to the end. Normally only called
  // when the user logs in during the quiz
  abstract refresh(loggedIn: boolean, answers: QuizAnswerMap): void

  // Using the [groupIdx, screenIdx] tuple, calculates the screen to show next
  // by skipping over the disabled screen. Return the same tuple format.
  nextEnabledScreen(curr: [number, number]): [number, number] {
    let nextGroupIdx = curr[0]
    let nextScreenIdx = curr[1] + 1

    const group = this.groups[nextGroupIdx]

    // Jump to the next group since we're already out of bound
    if (nextScreenIdx >= group.length) {
      nextGroupIdx += 1
      nextScreenIdx = 0
    }

    while (
      this.groups[nextGroupIdx][nextScreenIdx].disabled &&
      nextGroupIdx < this.groups.length
    ) {
      const group = this.groups[nextGroupIdx]
      if (nextScreenIdx === group.length - 1) {
        nextGroupIdx += 1
        nextScreenIdx = 0
      } else {
        nextScreenIdx += 1
      }
    }

    return [nextGroupIdx, nextScreenIdx]
  }

  initializeCriteriaFnMap(groups: Array<Array<QuizScreen>>) {
    for (const group of groups) {
      for (const screen of group) {
        const id = screen.type === 'generic' ? screen.id : screen.question.id
        this.criteriaMap[id] = this.buildCriteriaFn(id, screen.displayCriteria)
      }
    }
  }

  buildCriteriaFn(
    screenId: string,
    criteria?: QuestionCriteria | null,
  ): CriteriaFn {
    if (!criteria) {
      return alwaysTrue
    }

    switch (criteria.type) {
      case 'none':
        return alwaysTrue

      case 'not':
        const baseFn = this.buildCriteriaFn(screenId, criteria.child)
        return (loggedIn, answers) => !baseFn(loggedIn, answers)

      case 'and':
        const leftFn = this.buildCriteriaFn(screenId, criteria.left)
        const rightFn = this.buildCriteriaFn(screenId, criteria.right)
        return (loggedIn, answers) =>
          leftFn(loggedIn, answers) && rightFn(loggedIn, answers)

      case 'at-least-one-is-selected':
        return (_, answers) =>
          atLeastOneIsSelected(criteria.answers, answers?.[criteria.screenId])

      case 'is-true':
        return (_, answers) => isTrue(answers?.[criteria.screenId])

      case 'screen-should-be-enabled':
        const prior = this.criteriaMap[criteria.screenId]!
        return (loggedIn, answers) => prior(loggedIn, answers)

      case 'is-logged-in':
        return (loggedIn, _) => loggedIn
    }
  }
}

const alwaysTrue: CriteriaFn = () => true

// Mainly only the screenId is useful for the caller, and the indexes are to make
// the dynamic quiz object easier to traverse.
export interface Pointer {
  readonly screenId: string //  ID of the screen to be shown
  readonly groupIdx: number //  Index to the group
  readonly screenIdx: number // Index within the group to the question itself
}

export function ptr(
  screenId: string,
  groupIdx: number,
  screenIdx: number,
): Pointer {
  return { screenId, groupIdx, screenIdx }
}

// When computing which question to show next, not only do we have to return
// the pointer to the next question, but also which offset should the quiz be
// set to. Consider the case the user logs in or registers successfully
// during the quiz, even though they are, on an abstract level, going to the
// next question, the offset does not change.
export interface PointerWithOffset {
  pointer: Pointer
  offset: number
  screen: QuizScreen
}

// Sometimes, user might have logged in during the quiz, and we need to take this
// into consideration when calculating the next or previous question.
export interface EnrichedData<T> {
  authenticated: boolean
  data: T
}

export type CriteriaFn = (loggedIn: boolean, answers?: QuizAnswerMap) => boolean

export type CriteriaFnMap = {
  [key: string]: CriteriaFn
}
